import { z } from 'zod';

/**
 * GET {pos}/terminals/my — `{ data: Terminal[] }`, no pagination
 * (pos-api/app/Http/Controllers/Api/PosTerminalController.php:13-30).
 *
 * KNOWN BACKEND DEFECT (code-verified): the controller filters
 * `branch_id = $request->attributes->get('branch_id') ?? 1` but no middleware
 * ever sets that request ATTRIBUTE, so the endpoint always filters branch 1
 * and ignores X-Branch (PosTerminalController.php:16-22). The client shows
 * what the API returns and documents the gap — it must not fake terminals.
 *
 * Serialization (pos-api/app/Models/PosTerminal.php:23-32): is_active boolean
 * cast, type is the standard|admin enum, settings is an array cast with a
 * default permissions block.
 */

export const terminalTypeSchema = z.enum(['standard', 'admin']);
export type TerminalType = z.infer<typeof terminalTypeSchema>;

export const wireTerminalPermissionsSchema = z.looseObject({
  restrict_delivery_orders: z.boolean().optional(),
  restrict_pickup_orders: z.boolean().optional(),
  restrict_dinein_orders: z.boolean().optional(),
});

export const wireTerminalSchema = z.looseObject({
  id: z.number(),
  branch_id: z.number().nullable(),
  warehouse_id: z.number().nullable().optional(),
  name: z.string(),
  identifier: z.string(),
  type: terminalTypeSchema,
  floor: z.string().nullish(),
  is_active: z.boolean(),
  /*
    AN EMPTY `settings` ARRIVES AS `[]`, NOT `{}`.

    `settings` is an array cast on PosTerminal (pos-api/app/Models/PosTerminal.php:23-32),
    and PHP has one type for both maps and lists — so a terminal whose settings
    have never been written serializes as `[]`, while a configured one
    serializes as `{"permissions":{…}}`. This is the same quirk
    src/types/checkout.ts:290 already documents for order-item `attributes`.

    The schema demanded an object, so ONE unconfigured terminal anywhere in the
    response failed the whole `wireTerminalsMyResponseSchema.parse` and the
    setup screen showed a raw Zod blob instead of a terminal list — a workstation
    that cannot be set up because some OTHER till has no settings yet. Observed
    on `/setup` with `data[1].settings` as `[]`.

    So both shapes are accepted and an array is read as "nothing configured",
    which is exactly what it means. The union is deliberately not `z.unknown()`:
    a settings object that is present must still have its permissions validated,
    because `normalizeTerminal` hands them to the register as order-type
    restrictions.
  */
  settings: z
    .union([
      z.looseObject({ permissions: wireTerminalPermissionsSchema.nullish() }),
      z.array(z.unknown()).transform(() => ({}) as { permissions?: undefined }),
    ])
    .nullish(),
});
export type WireTerminal = z.infer<typeof wireTerminalSchema>;

export const wireTerminalsMyResponseSchema = z.looseObject({
  data: z.array(wireTerminalSchema),
});

/**
 * The management endpoints answer through the shared envelope and carry the
 * assignment rows; `identifier` is optional there because a till may be created
 * without one.
 */
export const wireManagedTerminalSchema = wireTerminalSchema.extend({
  has_fbr_token: z.boolean().optional(),
  fbr_pos_id: z.number().nullable().optional(),
  identifier: z.string().nullish(),
  assigned_users: z
    .array(
      z.looseObject({
        user_id: z.number(),
        is_primary: z.boolean().nullish(),
        assigned_at: z.string().nullish(),
      }),
    )
    .nullish(),
});

export const wireTerminalListResponseSchema = z.looseObject({
  data: z.looseObject({ terminals: z.array(wireManagedTerminalSchema) }),
});

export const wireTerminalWriteResponseSchema = z.looseObject({
  data: z.looseObject({ terminal: wireManagedTerminalSchema }),
});

export interface TerminalAssignment {
  userId: number;
  isPrimary: boolean;
}

export interface ManagedTerminal extends Terminal {
  has_fbr_token?: boolean;
  fbr_pos_id?: number | null;
  assignedUserIds: number[];
  assignments: TerminalAssignment[];
}

export function normalizeManagedTerminal(
  wire: z.infer<typeof wireManagedTerminalSchema>,
): ManagedTerminal {
  const assignments = (wire.assigned_users ?? []).map((row) => ({
    userId: row.user_id,
    isPrimary: !!row.is_primary,
  }));

  return {
    id: wire.id,
    branch_id: wire.branch_id,
    warehouse_id: wire.warehouse_id ?? null,
    name: wire.name,
    identifier: wire.identifier ?? '',
    type: wire.type,
    floor: wire.floor ?? null,
    is_active: wire.is_active,
    permissions: {
      restrict_delivery_orders: !!wire.settings?.permissions?.restrict_delivery_orders,
      restrict_pickup_orders: !!wire.settings?.permissions?.restrict_pickup_orders,
      restrict_dinein_orders: !!wire.settings?.permissions?.restrict_dinein_orders,
    },
    has_fbr_token: wire.has_fbr_token ?? false,
    fbr_pos_id: wire.fbr_pos_id ?? null,
    assignments,
    assignedUserIds: assignments.map((a) => a.userId),
  };
}

export interface TerminalWriteInput {
  fbr_pos_id?: number | null;
  fbr_token?: string | null;
  branch_id?: number | null;
  warehouse_id?: number | null;
  name: string;
  identifier?: string | null;
  type?: TerminalType;
  floor?: string | null;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Normalized domain types
// ---------------------------------------------------------------------------

export interface TerminalPermissions {
  restrict_delivery_orders: boolean;
  restrict_pickup_orders: boolean;
  restrict_dinein_orders: boolean;
}

export interface Terminal {
  id: number;
  branch_id: number | null;
  /** Physical stock location locked to this device for stock operations and sales. */
  warehouse_id: number | null;
  name: string;
  identifier: string;
  /** standard terminals see only their own orders; admin see all and skip the assignment check. */
  type: TerminalType;
  floor: string | null;
  is_active: boolean;
  permissions: TerminalPermissions;
}

/* ------------------------------------------------- printing configuration */

/**
 * Which Windows device prints what, for ONE till.
 *
 * WHY IT MOVED OFF THE DEVICE. This was `PrinterMappings` in the console's own
 * localStorage: invisible to anyone not sitting at the machine, gone on a fresh
 * browser profile, and impossible for an office to audit. It is a property of
 * the counter, so it lives on the terminal row that already describes one.
 *
 * WHY A REGISTRY RATHER THAN TWO SLOTS. The old shape had exactly one receipt
 * device, one kitchen device and a flat category→device map, so a printer had no
 * name and no identity — "the Bar printer" existed only as whichever categories
 * happened to point at the same string. Naming them is what makes an assignment
 * screen readable, and what lets a broken station be switched off without
 * unpicking every category that used it.
 *
 * A CATEGORY REACHES EXACTLY ONE PRINTER. `assignments` is categoryId → printer,
 * not a list. That keeps every routed group's job id unique without a printer
 * segment in the key: two tickets from one round can only differ by category, so
 * they can never compose the same id and be swallowed as a duplicate.
 */
export const printerRoleSchema = z.enum(['receipt', 'kot', 'invoice', 'packing']);
export type PrinterRole = z.infer<typeof printerRoleSchema>;

export const printerPaperSizeSchema = z.enum(['58mm', '80mm', 'A4']);
export type PrinterPaperSize = z.infer<typeof printerPaperSizeSchema>;
export const printerOrientationSchema = z.enum(['portrait', 'landscape']);
export type PrinterOrientation = z.infer<typeof printerOrientationSchema>;

/**
 * Financial documents intentionally have their own routing namespace. A sale
 * invoice is one document and must never be split by product collection.
 * Refund notes will be added only when the POS has an authoritative refund
 * transaction to print; a return-policy paragraph is not evidence of one.
 */
export const documentPrintTypeSchema = z.enum(['sale_invoice']);
export type DocumentPrintType = z.infer<typeof documentPrintTypeSchema>;

export const wireConfiguredPrinterSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  /** Exact Windows device name — never a display name; OpenPrinterW needs this. */
  device: z.string(),
  role: printerRoleSchema,
  /** Optional in v1; document printers in v2 are always A4. */
  paperSize: printerPaperSizeSchema.optional(),
  orientation: printerOrientationSchema.optional(),
  copies: z.number().int().min(1).max(3).optional(),
  /** KOT/packing only: the printer every unassigned route falls back to. */
  isDefault: z.boolean().catch(false),
  isActive: z.boolean().catch(true),
});

/**
 * Assignments arrive as a JSON object, so its keys are strings even though they
 * are category ids. Coerced here once rather than at every read site — a
 * `Record<string, …>` indexed by a number is a silent miss, not a type error.
 */
export const wirePrintingConfigSchema = z.looseObject({
  version: z.number().catch(1),
  printers: z.array(wireConfiguredPrinterSchema).catch([]),
  assignments: z.record(z.string(), z.string()).catch({}),
  packingAssignments: z.record(z.string(), z.string()).catch({}),
  documentAssignments: z.record(z.string(), z.string()).catch({}),
});

export const wirePrintingConfigResponseSchema = z.looseObject({
  data: z.looseObject({ printing: wirePrintingConfigSchema }),
});

export interface ConfiguredPrinter {
  id: string;
  name: string;
  device: string;
  role: PrinterRole;
  /** Normalized API reads include these; optional keeps hand-built v1 configs source-compatible. */
  paperSize?: PrinterPaperSize;
  orientation?: PrinterOrientation;
  copies?: number;
  isDefault: boolean;
  isActive: boolean;
}

export interface PrintingConfig {
  version: number;
  printers: ConfiguredPrinter[];
  /** categoryId → printer id. A category reaches at most one printer. */
  assignments: Record<number, string>;
  /**
   * collection/category id → packing printer id. This routes packing slips;
   * it never splits or duplicates the customer's financial invoice.
   */
  packingAssignments?: Record<number, string>;
  /** document type → document printer id. Currently only sale invoices. */
  documentAssignments?: Partial<Record<DocumentPrintType, string>>;
}

type LegacyConfiguredPrinter = Omit<ConfiguredPrinter, 'paperSize' | 'orientation' | 'copies'>;
type VersionTwoConfiguredPrinter = LegacyConfiguredPrinter & {
  paperSize: PrinterPaperSize;
  orientation: PrinterOrientation;
  copies: number;
};

export function emptyPrintingConfig(): PrintingConfig {
  return { version: 1, printers: [], assignments: {} };
}

export function normalizePrintingConfig(
  wire: z.infer<typeof wirePrintingConfigSchema>,
): PrintingConfig {
  const assignments: Record<number, string> = {};
  for (const [categoryId, printerId] of Object.entries(wire.assignments)) {
    const id = Number(categoryId);
    // A non-numeric key cannot name a category, so it can never match anything
    // at routing time. Dropping it keeps a hand-edited document from presenting
    // a mapping that silently does nothing.
    if (Number.isInteger(id)) assignments[id] = printerId;
  }

  const packingAssignments: Record<number, string> = {};
  for (const [collectionId, printerId] of Object.entries(wire.packingAssignments)) {
    const id = Number(collectionId);
    if (Number.isInteger(id)) packingAssignments[id] = printerId;
  }

  const documentAssignments: Partial<Record<DocumentPrintType, string>> = {};
  const saleInvoicePrinter = wire.documentAssignments.sale_invoice;
  if (saleInvoicePrinter) documentAssignments.sale_invoice = saleInvoicePrinter;

  return {
    version: wire.version,
    printers: wire.printers.map((printer) => ({
      id: printer.id,
      name: printer.name,
      device: printer.device,
      role: printer.role,
      paperSize:
        printer.role === 'invoice' || printer.role === 'packing'
          ? ('A4' as const)
          : (printer.paperSize ?? '80mm'),
      orientation: printer.orientation ?? 'portrait',
      copies: printer.copies ?? 1,
      // Enforced server-side too; mirrored here so a hand-edited document
      // cannot make the receipt device the kitchen fallback.
      isDefault: (printer.role === 'kot' || printer.role === 'packing') && printer.isDefault,
      isActive: printer.isActive,
    })),
    assignments,
    ...(wire.version >= 2 || Object.keys(packingAssignments).length > 0
      ? { packingAssignments }
      : {}),
    ...(wire.version >= 2 || Object.keys(documentAssignments).length > 0
      ? { documentAssignments }
      : {}),
  };
}

/** The wire body for a write. Mirrors what the endpoint validates. */
export function serializePrintingConfig(config: PrintingConfig): {
  version: 2;
  printers: VersionTwoConfiguredPrinter[];
  assignments: Record<string, string>;
  packingAssignments?: Record<string, string>;
  documentAssignments?: Partial<Record<DocumentPrintType, string>>;
} {
  const assignments: Record<string, string> = {};
  for (const [categoryId, printerId] of Object.entries(config.assignments)) {
    assignments[String(categoryId)] = printerId;
  }
  const packingAssignments: Record<string, string> = {};
  for (const [collectionId, printerId] of Object.entries(config.packingAssignments ?? {})) {
    packingAssignments[String(collectionId)] = printerId;
  }

  return {
    version: 2,
    printers: config.printers.map((printer): VersionTwoConfiguredPrinter => ({
      id: printer.id,
      name: printer.name,
      device: printer.device,
      role: printer.role,
      paperSize:
        printer.role === 'invoice' || printer.role === 'packing'
          ? 'A4'
          : (printer.paperSize ?? '80mm'),
      orientation: printer.orientation ?? 'portrait',
      copies: Math.min(3, Math.max(1, printer.copies ?? 1)),
      isDefault: printer.isDefault,
      isActive: printer.isActive,
    })),
    assignments,
    packingAssignments,
    documentAssignments: { ...(config.documentAssignments ?? {}) },
  };
}

export function normalizeTerminal(wire: WireTerminal): Terminal {
  const p = wire.settings?.permissions ?? undefined;
  return {
    id: wire.id,
    branch_id: wire.branch_id,
    warehouse_id: wire.warehouse_id ?? null,
    name: wire.name,
    identifier: wire.identifier,
    type: wire.type,
    floor: wire.floor ?? null,
    is_active: wire.is_active,
    permissions: {
      restrict_delivery_orders: p?.restrict_delivery_orders ?? false,
      restrict_pickup_orders: p?.restrict_pickup_orders ?? false,
      restrict_dinein_orders: p?.restrict_dinein_orders ?? false,
    },
  };
}
